import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, CalendarDays, ChevronDown, Ticket } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { SectionHeading } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DIFFS = ["all", "soft", "trail", "extreme"];
const DIFF_STYLE = {
  soft: "text-[#7FB383] border-[#4E8752]/50 bg-[#4E8752]/10",
  trail: "text-ochre border-ochre/50 bg-ochre/10",
  extreme: "text-[#E86A50] border-[#D94235]/50 bg-[#D94235]/10",
};

export default function Events() {
  const { t, lang } = useLanguage();
  const { user, setAuthOpen } = useAuth();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [booked, setBooked] = useState({});
  const [waitlisted, setWaitlisted] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/events`)
      .then((r) => setEvents(r.data))
      .catch(() => toast.error(t.events.loadError))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) {
      setWaitlisted({});
      return;
    }
    axios
      .get(`${API}/members/me/waitlist`, { withCredentials: true })
      .then((r) => setWaitlisted(Object.fromEntries(r.data.map((id) => [id, true]))))
      .catch(() => {});
  }, [user]);

  const joinWaitlist = async (ev) => {
    if (!user) {
      toast(t.events.loginToBook);
      setAuthOpen(true);
      return;
    }
    try {
      await axios.post(`${API}/events/${ev.id}/waitlist?lang=${lang}`, {}, { withCredentials: true });
      setWaitlisted((p) => ({ ...p, [ev.id]: true }));
      toast.success(t.events.waitlistSuccess);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t.events.loadError);
    }
  };

  const book = async (ev) => {
    if (!user) {
      toast(t.events.loginToBook);
      setAuthOpen(true);
      return;
    }
    try {
      const { data } = await axios.post(`${API}/events/${ev.id}/book?lang=${lang}`, {}, { withCredentials: true });
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, spots_taken: data.spots_taken } : e)));
      setBooked((prev) => ({ ...prev, [ev.id]: true }));
      toast.success(t.events.bookSuccess);
      if (data.rig_warning) toast(t.member.rigWarn);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t.events.loadError);
    }
  };

  const fmt = (iso) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
      day: "2-digit",
      month: "short",
    });

  const filtered = filter === "all" ? events : events.filter((e) => e.difficulty === filter);

  return (
    <section id="eventi" className="py-28 sm:py-36" data-testid="events-section">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHeading eyebrow={t.events.eyebrow} title={t.events.title} />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap gap-2 mb-14 sm:mb-20"
          >
            {DIFFS.map((d) => (
              <button
                key={d}
                data-testid={`event-filter-difficulty-${d}`}
                onClick={() => setFilter(d)}
                className={`font-telemetry text-[10px] sm:text-[11px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-200 ${
                  filter === d
                    ? "bg-blaze text-ink border-blaze"
                    : "border-line text-sandmut hover:border-blaze hover:text-blaze"
                }`}
              >
                {d === "all" ? t.events.filterAll : t.events.difficulties[d]}
              </button>
            ))}
          </motion.div>
        </div>

        {loading && (
          <div className="font-telemetry text-xs uppercase tracking-[0.25em] text-sandmut py-16 text-center">
            …
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <p className="font-telemetry text-xs uppercase tracking-[0.25em] text-sandmut py-16 text-center">
            {t.events.empty}
          </p>
        )}

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((ev, i) => {
              const left = ev.spots_total - ev.spots_taken;
              const soldOut = left <= 0;
              const isBooked = !!booked[ev.id];
              const isWaitlisted = !!waitlisted[ev.id];
              const open = openId === ev.id;
              return (
                <motion.article
                  key={ev.id}
                  layout
                  data-testid={`event-item-card-${ev.id}`}
                  initial={{ opacity: 0, y: 44 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.7, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                  className="group relative border border-line bg-surface overflow-hidden flex flex-col"
                >
                  <div className="relative h-52 overflow-hidden">
                    <Link to={`/eventi/${ev.id}`} data-testid={`event-open-detail-${ev.id}`} className="block h-full">
                      <img
                        src={ev.image}
                        alt={ev.title[lang]}
                        loading="lazy"
                        className="w-full h-full object-cover warm-grade group-hover:scale-105 transition-transform duration-700"
                      />
                    </Link>
                    <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent pointer-events-none" />
                    <div className="absolute top-3 left-3 font-telemetry text-[10px] tracking-[0.15em] uppercase bg-ink/75 backdrop-blur-sm border border-sand/15 text-sand px-3 py-1.5">
                      {fmt(ev.date)} — {ev.days} {ev.days > 1 ? t.events.days : t.events.day}
                    </div>
                    <div
                      className={`absolute top-3 right-3 font-telemetry text-[10px] tracking-[0.15em] uppercase border px-3 py-1.5 backdrop-blur-sm ${DIFF_STYLE[ev.difficulty]}`}
                    >
                      {t.events.difficulties[ev.difficulty]}
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col p-6">
                    <Link to={`/eventi/${ev.id}`}>
                      <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-bone leading-tight hover:text-blaze transition-colors">
                        {ev.title[lang]}
                      </h3>
                    </Link>
                    <div className="mt-2 flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.15em] text-ochre">
                      <MapPin size={12} />
                      {ev.location}
                      <span className="text-faint">/</span>
                      {ev.coords}
                    </div>
                    <p className="mt-3 text-xs sm:text-sm leading-relaxed text-ash">
                      {ev.description[lang]}
                    </p>

                    <button
                      data-testid={`event-itinerary-toggle-${ev.id}`}
                      onClick={() => setOpenId(open ? null : ev.id)}
                      className="mt-4 flex items-center gap-2 font-telemetry text-[10px] uppercase tracking-[0.22em] text-sandmut hover:text-blaze transition-colors w-max"
                    >
                      {open ? t.events.close : t.events.itinerary}
                      <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {open && (
                        <motion.ol
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="mt-3 border-l-2 border-blaze/40 pl-4 flex flex-col gap-2">
                            {ev.itinerary[lang].map((step, j) => (
                              <li key={j} className="text-xs text-sand/80 leading-relaxed list-none">
                                <span className="font-telemetry text-blaze mr-2">{String(j + 1).padStart(2, "0")}</span>
                                {step}
                              </li>
                            ))}
                          </div>
                        </motion.ol>
                      )}
                    </AnimatePresence>

                    <div className="mt-auto pt-5">
                      <div className="flex items-center justify-between font-telemetry text-[10px] uppercase tracking-[0.18em] text-sandmut mb-2">
                        <span className="flex items-center gap-1.5">
                          <CalendarDays size={11} />
                          {soldOut ? t.events.soldOut : `${left} ${t.events.spotsLeft}`}
                        </span>
                        <span className="text-sand">
                          {t.events.from} €{ev.price}
                        </span>
                      </div>
                      <div className="h-[3px] bg-line w-full mb-4">
                        <div
                          className={`h-full ${soldOut ? "bg-[#D94235]" : "bg-blaze"}`}
                          style={{ width: `${Math.min(100, (ev.spots_taken / ev.spots_total) * 100)}%` }}
                        />
                      </div>
                      <button
                        data-testid={`event-book-action-btn-${ev.id}`}
                        onClick={() => (soldOut && !isBooked ? joinWaitlist(ev) : book(ev))}
                        disabled={isBooked || isWaitlisted}
                        className={`w-full font-telemetry text-[11px] uppercase tracking-[0.22em] px-5 py-3.5 flex items-center justify-center gap-2.5 transition-colors duration-300 ${
                          isBooked
                            ? "bg-[#4E8752]/20 text-[#7FB383] border border-[#4E8752]/40 cursor-default"
                            : isWaitlisted
                            ? "bg-ochre/15 text-ochre border border-ochre/40 cursor-default"
                            : soldOut
                            ? "border border-blaze/60 text-blaze hover:bg-blaze hover:text-ink"
                            : "bg-blaze text-ink hover:bg-sand"
                        }`}
                      >
                        <Ticket size={14} />
                        {isBooked ? t.events.booked : isWaitlisted ? t.events.inWaitlist : soldOut ? t.events.joinWaitlist : t.events.book}
                      </button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
